import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { useOffers } from './useOffers';
import { useSplitTestBranch } from '../../api/lendingApi/lendingApiQueries';
import { getMarketingPollUpdatedState } from '../../components/helpers/stateNormalisation';
import { ISOLATED_SPLIT_TESTS } from '../../constants';
import { TGetStateResponseDE } from '../../lapiOverrideTypesDE';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupParams } from '../useSignupParams';
import { useSignupRouterDE } from '../useSignupRouter/useSignupRouterDE';

const OffersDEContext = createContext<
  | {
      signupError: ReturnType<ReturnType<typeof useOffers>['getSignupError']>;
      offers: ReturnType<typeof useOffers>['offers'];
      documentRequirements: ReturnType<
        ReturnType<typeof useOffers>['getDocumentRequirements']
      >;
      handleDecision: () => Promise<void>;
      isDeciding: boolean;
      isLoadingOffers: boolean;
      isRefetchingOffers: boolean;
      isLoadingOrRedirecting: boolean;
      isSyncingState: boolean;
      setIsSyncingState: Dispatch<SetStateAction<boolean>>;
      isInExitPollTest: boolean;
    }
  | undefined
>(undefined);

type TProps = {
  children: React.ReactNode;
};

export const OffersDEProvider = ({ children }: TProps) => {
  const [isDeciding, setIsDeciding] = useState(false);
  const [isSyncingState, setIsSyncingState] = useState(false);

  const {
    isLoadingOffers,
    getSignupError,
    offers,
    getDocumentRequirements,
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus,
  } = useOffers({ enabled: true });

  const stateKey = useContextStateKey();
  const { navigateSignupDE, navigateToAccountPages, currentStep } =
    useSignupRouterDE();

  const { affiliateTag } = useSignupParams();

  const hasDocumentsRequired = checkDocumentsRequired(
    getDocumentRequirements(offers),
  );

  const isEbayCustomer = affiliateTag === 'ebay';
  const isOffersOrEligibilityStep =
    currentStep === 'angebote' || currentStep === 'eignungscheck';

  const {
    splitTestBranch: eligibilityTestBranch,
    isLoading: eligibilityTestLoading,
  } = useSplitTestBranch('de_relaunch_eligibility_page_test_2026_q1', {
    enabled:
      hasDocumentsRequired && !isEbayCustomer && isOffersOrEligibilityStep,
    cookieOverride: true,
  });

  const { splitTestBranch: exitPollBranch } = useSplitTestBranch(
    ISOLATED_SPLIT_TESTS.DE_EXIT_POLL_TEST,
    { enabled: false, cookieOverride: true },
  );
  const isInExitPollTest = exitPollBranch === 'INCLUDE';

  useEffect(() => {
    if (exitPollBranch) {
      tracking.ampli.addedToSplitTest({
        Branch: exitPollBranch,
        'Test name': ISOLATED_SPLIT_TESTS.DE_EXIT_POLL_TEST,
      });
    }
  }, [exitPollBranch]);

  // Redirection useEffect
  useEffect(() => {
    if (
      !isOffersOrEligibilityStep ||
      !stateKey ||
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      isDeciding ||
      eligibilityTestLoading
    ) {
      return;
    }

    const signupError = getSignupError(offers);

    if (signupError && signupError !== 'complete_personal_credit_check') {
      return;
    }

    const updatedState = getMarketingPollUpdatedState<TGetStateResponseDE>();

    if (eligibilityTestBranch === 'INCLUDE') {
      void navigateSignupDE('eignungscheck', stateKey, updatedState);
    } else if (hasDocumentsRequired) {
      void navigateSignupDE('schritt-3', stateKey, updatedState);
    } else {
      void navigateToAccountPages(stateKey, updatedState);
    }
  }, [isLoadingOffers, offers, stateKey, eligibilityTestLoading]);

  const handleDecision = async () => {
    setIsDeciding(true);

    const refetchedOffers = await refetchOffers();

    const hasRefetchedDocumentsRequired = checkDocumentsRequired(
      getDocumentRequirements(refetchedOffers),
    );

    if (!hasRefetchedDocumentsRequired && stateKey) {
      await navigateToAccountPages(
        stateKey,
        getMarketingPollUpdatedState<TGetStateResponseDE>(),
      );
    }

    setIsDeciding(false);
  };

  return (
    <OffersDEContext.Provider
      value={{
        signupError: getSignupError(offers),
        offers,
        documentRequirements: getDocumentRequirements(offers),
        handleDecision,
        isDeciding,
        isLoadingOffers,
        isRefetchingOffers,
        isLoadingOrRedirecting:
          isLoadingOffers ||
          !offers ||
          getSignupError(offers) === 'complete_personal_credit_check' ||
          offerStatus === 'declined' ||
          offerStatus === 'deferred',
        isSyncingState,
        setIsSyncingState,
        isInExitPollTest,
      }}
    >
      {children}
    </OffersDEContext.Provider>
  );
};

export function useOffersDE() {
  const context = useContext(OffersDEContext);
  if (context === undefined) {
    throw new Error('useOffersDE must be used within the OffersDEProvider');
  }
  return context;
}
